import {
	Form,
	FormItem,
	ReorderableList
} from "client/Components";
import {
	AppDestinationWindowSelectFormItem
} from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import {
	AddTilesResponseModel,
	MagisterAppDetailsTileModel,
	UpdateMagisterTabModel,
	MagisterUpdateTileModel,
	updateMagisterTile
} from "client/Api/Workspace/workspace";
import { IFormHandler, useFormHandler } from "client/Forms";
import { IValueInputAttachable } from "client/Forms/attachable";
import { generateTrackingId } from "client/Misc/utils";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { newGuid } from "client_legacy/ndaw/guid";
import { ExistingTile } from "../common";
import { EditTileFromData, initialGenericState } from "../EditGenericTile";
import { EditTileHandle } from "../EditTile";
import { EditTileGeneralFields } from "../GeneralFields";
import { useUpdateTile } from "../hooks/useUpdateTile";
import { EditMagisterTabItem } from "./EditMagisterTabItem";

export type EditMagisterTileProps = {
	initialModel: MagisterAppDetailsTileModel;
	mode: ExistingTile;
	onIsLoadingChange(isLoading: boolean): void;
	isLoading: boolean;
	onAfterSave: (response?: AddTilesResponseModel) => void;
};

export const EditMagisterTile = forwardRef(
	(props: EditMagisterTileProps, ref: Ref<EditTileHandle>): ReactElement => {
		const {
			initialModel,
			mode,
			onIsLoadingChange,
			isLoading,
			onAfterSave,
		} = props;

		const [formData, setFormData] = useState<MagisterEditFormData>({
			...initialGenericState(initialModel),
			destinationWindow: initialModel.destinationWindow,
			tabs: [...initialModel.tabs]
		});

		const formHandler: IFormHandler<MagisterEditFormData> = useFormHandler(
			formData,
			setFormData,
			validationBuilder
		);

		const { columnSpan, rowSpan } = formHandler.for("dimension")
			.value;

		const areTabsNeeded: boolean = useMemo(
			(): boolean => computeAreTabNeeded(columnSpan, rowSpan),
			[columnSpan, rowSpan]
		);

		const { updateHandler } = useUpdateTile<MagisterUpdateTileModel>(
			formHandler,
			updateMagisterTile,
			isLoading,
			onIsLoadingChange,
			mode,
			onAfterSave,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditTileHandle => ({
				updateHandler
			})
		);

		const itemMoveHandler = useCallback(
			(fromIndex: number, toIndex: number): void => {
				setFormData(
					(formState) => {
						const tabsCopy: UpdateMagisterTabModel[] = [...formState.tabs];
						const item: UpdateMagisterTabModel = tabsCopy.splice(fromIndex, 1)[0];
						tabsCopy.splice(toIndex, 0, item);

						return {
							...formState,
							tabs: tabsCopy
						};
					}
				);
			},
			[]
		);

		const addTabHandler = (): void => {
			const newTab: UpdateMagisterTabModel = {
				name: "",
				resourceKind: undefined,
				uniqueId: newGuid()
			};
			const tabsHandler: IValueInputAttachable<UpdateMagisterTabModel[]> = formHandler.for("tabs");

			tabsHandler.reducer
				.push(newTab);

			tabsHandler.validation
				.triggerInputFinished();
		};

		const removeTabHandler = (index: number): void => {
			const tabsHandler: IValueInputAttachable<UpdateMagisterTabModel[]> = formHandler.for("tabs");

			const hasMoreThenOneTab: boolean = tabsHandler
				.value
				.length > 1;

			if (hasMoreThenOneTab) {
				tabsHandler.reducer
					.removeAt(index);

				tabsHandler.validation
					.triggerInputFinished();
			}
		};

		return (
			<Form onSubmit="NONE">
				<EditTileGeneralFields
					initialModel={initialModel}
					formHandler={formHandler}
				/>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{areTabsNeeded && (
					<FormItem hasNoMargin>
						<ReorderableList onItemMove={itemMoveHandler}>
							{formHandler.for("tabs")
								.map((tabHandler, index) => (
									<EditMagisterTabItem
										key={tabHandler.value
											.uniqueId}
										onRemove={() => removeTabHandler(index)}
										attachable={tabHandler}
									/>
								))
							}
						</ReorderableList>
						{formData.tabs.length < MAX_TAB_NUMBER && (
							<ReorderableList.AddButton
								trackingId={generateTrackingId("edit-magister-tile", "__add-tab-button")}
								onClick={addTabHandler}
							/>
						)}
					</FormItem>
				)}
			</Form>
		);
	}
);

EditMagisterTile.displayName = "EditMagisterTile";

function computeAreTabNeeded(columnSpan: number, rowSpan: number): boolean {
	return columnSpan >= 4 && rowSpan >= 4;
}

function prepareModelForSaving(formData: MagisterEditFormData): MagisterEditFormData {
	const { columnSpan, rowSpan } = formData.dimension;
	const areTabsNeeded = computeAreTabNeeded(columnSpan, rowSpan);

	return {
		...formData,
		tabs: areTabsNeeded ? formData.tabs : []
	};
}

const MAX_TAB_NUMBER = 20;

type MagisterEditFormData = EditTileFromData<MagisterUpdateTileModel>;

const validationBuilder: IValidationConfiguration<MagisterEditFormData> =
	createValidationConfiguration<MagisterEditFormData>();

validationBuilder.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

const tabValidation = validationBuilder
	.when(
		(formData): boolean => {
			const { columnSpan, rowSpan } = formData.dimension;
			return computeAreTabNeeded(columnSpan, rowSpan);
		}
	)
	.forCollection("tabs")
	.forItems(
		item => item.uniqueId
	);

tabValidation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

tabValidation.forScalar("resourceKind")
	.add(
		isNotNullNorUndefined()
	);
